import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Pagination, Modal, Form, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import PostCard from '../../components/PostCard/PostCard';
import './index.css';

const { Search } = Input;

export default function MyPosts({ posts = [], loggedInUser, onDelete, onUpdateUser }) {
  const themeOptions = [
    { id: 'theme-default', name: '蓝黄', top: '#1890ff', bottom: '#ffd666' },
    { id: 'theme-indigo', name: '靛粉', top: '#5b8def', bottom: '#ffd6e7' },
    { id: 'theme-rose', name: '玫橙', top: '#ff6b6b', bottom: '#ffd9b3' },
    { id: 'theme-teal', name: '青绿', top: '#12b886', bottom: '#d4f5e9' },
    { id: 'theme-01847f', name: '绿橙', top: '#01847F', bottom: '#FFAA93' }
  ];
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('siteTheme') || 'theme-default';
    } catch (e) { return 'theme-default'; }
  });

  useEffect(() => {
    const root = document.documentElement;
    // remove existing theme- classes
    Array.from(root.classList).forEach((c) => { if (c.startsWith('theme-')) root.classList.remove(c); });
    root.classList.add(theme);
    try { localStorage.setItem('siteTheme', theme); } catch(e) {}
  }, [theme]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(5);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  
  // compute posts even if not logged in to keep hooks order stable
  const myPostsAll = useMemo(() => {
    if (!loggedInUser) return [];
    return posts.filter((p) => p.authorId === loggedInUser.id);
  }, [posts, loggedInUser]);

  const filtered = useMemo(() => {
    const q = (searchQuery || '').toLowerCase();
    if (!q) return myPostsAll;
    return myPostsAll.filter((p) => {
      return (
        (p.title && p.title.toLowerCase().includes(q)) ||
        (p.summary && p.summary.toLowerCase().includes(q)) ||
        (Array.isArray(p.tags) && p.tags.join(' ').toLowerCase().includes(q))
      );
    });
  }, [myPostsAll, searchQuery]);

  const total = filtered.length;
  const start = (currentPage - 1) * pageSize;
  const pageItems = filtered.slice(start, start + pageSize);
  
  const showModal = () => {
    form.setFieldsValue({
      name: loggedInUser?.name || '',
      avatar: loggedInUser?.avatar ? [{ uid: '-1', name: 'avatar.png', status: 'done', url: loggedInUser.avatar }] : [],
      background: loggedInUser?.background ? [{ uid: '-1', name: 'background.png', status: 'done', url: loggedInUser.background }] : []
    });
    setIsModalVisible(true);
  };

  const normFile = (e) => {
    if (!e) return [];
    if (Array.isArray(e)) return e;
    return e && e.fileList;
  };

  // helper: controlled search input
  const onSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleOk = () => {
    form.validateFields().then(async (values) => {
      // 处理头像文件
      let avatarUrl = loggedInUser?.avatar;
      if (values.avatar && values.avatar.length > 0) {
        const avatarFile = values.avatar[0].originFileObj || values.avatar[0].url;
        if (avatarFile && typeof avatarFile !== 'string') {
          // 是一个文件对象，转换为base64
          avatarUrl = await readFileAsDataURL(avatarFile);
        } else if (typeof avatarFile === 'string') {
          // 已经是URL
          avatarUrl = avatarFile;
        }
      }

      // 处理背景图片文件
      let backgroundUrl = loggedInUser?.background;
      if (values.background && values.background.length > 0) {
        const bgFile = values.background[0].originFileObj || values.background[0].url;
        if (bgFile && typeof bgFile !== 'string') {
          // 是一个文件对象，转换为base64
          backgroundUrl = await readFileAsDataURL(bgFile);
        } else if (typeof bgFile === 'string') {
          // 已经是URL
          backgroundUrl = bgFile;
        }
      }

      if (onUpdateUser) {
        onUpdateUser({
          ...loggedInUser,
          name: values.name,
          avatar: avatarUrl,
          background: backgroundUrl
        });
        message.success('用户信息已更新');
      }
      setIsModalVisible(false);
    });
  };

  const readFileAsDataURL = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };
  
  if (!loggedInUser) return <div className="page-container">请先登录以查看您的文章。</div>;

  return (
    <div className="myposts-container">
      <div className="myposts-header">
        <div className="myposts-usercard" style={{ backgroundImage: loggedInUser?.background ? `url(${loggedInUser.background})` : 'none' }}>
          <div className="myposts-userinner">
            {loggedInUser.avatar ? <img src={loggedInUser.avatar} alt="avatar" className="myposts-avatar" /> : <div className="myposts-avatar-placeholder">{(loggedInUser.name || '').slice(0,1)}</div>}
            <div className="myposts-userinfo">
              <h2 className="myposts-username">{loggedInUser.name}</h2>
              <div className="myposts-stats">共 {myPostsAll.length} 篇文章</div>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div className="theme-swatch-row" title="选择主题配色">
            {themeOptions.map((t) => (
              <button
                key={t.id}
                aria-label={t.name}
                className={`theme-swatch ${theme === t.id ? 'selected' : ''}`}
                onClick={() => setTheme(t.id)}
                style={{ padding: 0, border: 'none', background: 'transparent' }}
              >
                <div className="swatch-top" style={{ background: t.top }} />
                <div className="swatch-bottom" style={{ background: t.bottom }} />
              </button>
            ))}
          </div>

          <Button type="primary" onClick={showModal}>
            编辑个人信息
          </Button>
        </div>
      </div>
      
      <Modal
        title="编辑个人信息"
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="用户名"
            name="name"
            rules={[{ required: true, message: '请输入用户名!' }]}
          >
            <Input />
          </Form.Item>
          
          <Form.Item
            label="头像"
            name="avatar"
            valuePropName="fileList"
            getValueFromEvent={normFile}
          >
            <Upload 
              listType="picture" 
              maxCount={1}
              accept="image/*"
              beforeUpload={() => false}
            >
              <Button icon={<UploadOutlined />}>上传头像</Button>
            </Upload>
          </Form.Item>
          
          <Form.Item
            label="博客背景图"
            name="background"
            valuePropName="fileList"
            getValueFromEvent={normFile}
          >
            <Upload 
              listType="picture" 
              maxCount={1}
              accept="image/*"
              beforeUpload={() => false}
            >
              <Button icon={<UploadOutlined />}>上传背景图</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>
      
      <div style={{ marginBottom: 12, display: 'flex', gap: 12 }}>
        <Search placeholder="按标题/摘要/标签搜索" value={searchQuery} onChange={onSearchChange} enterButton allowClear />
      </div>

      {pageItems.length === 0 ? (
        <p>没有匹配的文章。</p>
      ) : (
        <div className="article-list">
          {pageItems.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              actions={(
                <div style={{ display: 'flex', gap: 8 }}>
                  <Link to={`/edit/${p.id}`}>编辑</Link>
                  <Button danger size="small" onClick={() => onDelete && onDelete(p.id)}>删除</Button>
                </div>
              )}
            />
          ))}
        </div>
      )}

      {total > pageSize && (
        <div style={{ textAlign: 'right', marginTop: 12 }}>
          <Pagination current={currentPage} pageSize={pageSize} total={total} onChange={(p) => setCurrentPage(p)} />
        </div>
      )}
    </div>
  );
}